import Image from "next/image"
import type { AddOnId } from "@/lib/quiz/types"

/**
 * Map each add-on to the product thumbnail used by the booking flow
 * (`booking.aheadhealth.com/public/<file>`). Sourced from the Supabase
 * seed files in ahead-booking-new — `images` field on each product row
 * in `supabase/seeds/01-base-data.sql` and `06-blood-only.sql`.
 *
 * Trend overlay has no product photo (it's a free analysis layer
 * over the selected blood tests), so it renders as a neutral icon
 * tile instead.
 */
const ADD_ON_THUMBNAIL: Partial<Record<AddOnId, string>> = {
  hormone_test: "https://booking.aheadhealth.com/Hormones.png",
  omega_test: "https://booking.aheadhealth.com/Vitamins.png",
  b_vitamins: "https://booking.aheadhealth.com/Vitamins.png",
  calcium_score: "https://booking.aheadhealth.com/Heart-CT.jpg",
  dexa_scan: "https://booking.aheadhealth.com/Dexa.jpg",
  mamma_mri: "https://booking.aheadhealth.com/Mamma.jpg",
}

const THUMBNAIL_CLASS =
  "relative block h-10 w-10 flex-shrink-0 overflow-hidden rounded-[6px] bg-neutral-100"

/** Three ascending bars for the trend overlay tile — classic
 *  "data trend" glyph. No axis lines / fills, so it reads cleaner
 *  inside the 40px tile than an area chart. */
function TrendIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
      className="text-neutral-600"
    >
      <path d="M6 20V13" />
      <path d="M12 20V9" />
      <path d="M18 20V5" />
    </svg>
  )
}

/**
 * Renders the booking-app product thumbnail inside a small rounded
 * square — same visual treatment the booking-app uses on its
 * "Select add-ons" step. Trend overlay falls back to a neutral icon
 * tile (no product photo).
 */
export function AddOnCategoryIcon({ id, alt }: { id: AddOnId; alt: string }) {
  const src = ADD_ON_THUMBNAIL[id]
  if (!src) {
    return (
      <span
        className={THUMBNAIL_CLASS + " flex items-center justify-center"}
        aria-label={alt}
      >
        <TrendIcon />
      </span>
    )
  }
  return (
    <span className={THUMBNAIL_CLASS}>
      <Image src={src} alt={alt} fill sizes="48px" className="object-cover" />
    </span>
  )
}
